<template>
  <div class="ctc-step">
    <div class="ctc-step__eyebrow">PHASE 1 · ÉTAPE 1 SUR 4</div>
    <h2 class="ctc-step__title">Où publier et quel produit traiter ?</h2>
    <rmr-notice
      v-if="fromOpportunity"
      variant="info"
      text="Source issue d’une opportunité Affilizz synchronisée"
      class="mb-4"
    />

    <section class="ctc-site-selection">
      <header class="ctc-site-selection__header">
        <span class="ctc-site-selection__icon">
          <v-icon color="#6366F1">{{ icons.mdiWeb }}</v-icon>
        </span>
        <span>
          <strong>Site de publication</strong>
          <small>
            Le site détermine le média utilisé pour l’analyse et le tracking.
          </small>
        </span>
      </header>

      <div v-if="selectedSite && !choosingSite" class="ctc-selected-site">
        <span class="ctc-selected-site__check" aria-hidden="true">✓</span>
        <span class="ctc-selected-site__content">
          <strong>{{ selectedSite.name }}</strong>
          <small>{{ selectedSite.domain }}</small>
        </span>
        <span class="ctc-selected-site__media">
          Média Affilizz : {{ selectedSite.affilizzMediaName }}
        </span>
        <rmr-action-button
          label="Changer de site"
          :icon="icons.mdiPencil"
          variant="ghost"
          size="sm"
          @click="choosingSite = true"
        />
      </div>

      <rmr-site-picker
        v-else
        :value="siteId"
        :sites="sites"
        :search-keys="['name', 'domain', 'affilizzMediaName']"
        search-placeholder="Rechercher un site, un domaine ou un média Affilizz…"
        aria-label="Site de publication"
        @input="requestSiteChange"
      >
        <template #metadata="{ site }">
          <span class="ctc-site-selection__mapping-dot" />
          Média Affilizz : {{ site.affilizzMediaName }}
        </template>
      </rmr-site-picker>
    </section>

    <section
      v-if="!fromOpportunity && siteId"
      ref="sourcePanel"
      class="ctc-source-panel"
    >
      <header class="ctc-source-panel__header">
        <span class="ctc-source-panel__icon">
          <v-icon color="#6366F1">{{ icons.mdiLinkVariant }}</v-icon>
        </span>
        <span>
          <strong>Importer une fiche produit</strong>
          <small
            >La récupération réelle est disponible pour Amazon.fr et
            Fnac.</small
          >
        </span>
      </header>

      <form class="ctc-source" @submit.prevent="$emit('analyze')">
        <rmr-text-field
          ref="sourceInput"
          :value="sourceUrl"
          label="URL de la fiche produit"
          placeholder="https://www.amazon.fr/dp/… ou https://www.fnac.com/a…"
          :error-messages="urlError ? [urlError] : []"
          :hide-details="false"
          :prepend-inner-icon="icons.mdiLinkVariant"
          class="ctc-source__input"
          size="md"
          @input="$emit('update:sourceUrl', $event)"
        />
        <rmr-action-button
          type="submit"
          label="Récupérer les informations"
          :icon="icons.mdiMagnify"
          :loading="analyzing"
          :disabled="!canAnalyze"
          class="ctc-source__submit"
        />
      </form>
    </section>

    <div v-if="analyzing" class="ctc-step__block" role="status">
      <div class="ctc-step__loading ctc-step__loading--product">
        <v-progress-circular indeterminate color="primary" size="34" />
        <span>
          <strong>Analyse de la fiche produit en cours…</strong>
          <small>
            La source produit est interrogée en temps réel. Cette opération peut
            prendre jusqu’à 30 secondes.
          </small>
        </span>
      </div>
      <v-skeleton-loader type="list-item-avatar-two-line, article" />
    </div>

    <rmr-notice
      v-else-if="analysisError"
      variant="error"
      :title="errorTitle"
      class="ctc-step__block"
    >
      {{ analysisError.message }}
      <div v-if="retryableError" class="ctc-step__notice-actions">
        <rmr-action-button
          label="Réessayer"
          :icon="icons.mdiRefresh"
          variant="ghost"
          size="sm"
          @click="$emit('analyze')"
        />
      </div>
    </rmr-notice>

    <div v-else-if="product" class="ctc-step__block">
      <!-- Blocking result first: visible without scrolling the product card. -->
      <rmr-notice
        v-if="noUsableOffer"
        variant="error"
        title="Aucune offre Affilizz utilisable"
        role="alert"
        class="ctc-source__no-offer"
      >
        {{ noOfferExplanation }}
        <div class="ctc-step__notice-actions">
          <rmr-action-button
            label="Réessayer"
            :icon="icons.mdiRefresh"
            variant="ghost"
            size="sm"
            :loading="checkingOffer"
            @click="$emit('retry-offer-check')"
          />
          <rmr-action-button
            label="Changer de produit"
            :icon="icons.mdiPencil"
            variant="ghost"
            size="sm"
            @click="changeProduct"
          />
        </div>
      </rmr-notice>
      <rmr-notice
        v-for="warning in product.warnings || []"
        :key="warning"
        variant="warning"
        :text="warning"
        class="ctc-source__warning"
      />
      <ctc-product-card
        :product="product"
        :errors="productErrors"
        :from-opportunity="fromOpportunity"
        editable
        @update-field="
          (field, value) => $emit('update-product-field', field, value)
        "
      />
      <ctc-offer-check-status
        v-if="!noUsableOffer"
        :offer-check="offerCheck"
        :checking="checkingOffer"
        :error="offerCheckError"
        :site="selectedSite"
        compact
        retryable
        class="ctc-step__offer-check"
        @retry="$emit('retry-offer-check')"
      />
    </div>

    <rmr-confirm-dialog
      v-model="confirmingSiteChange"
      title="Changer de site ?"
      message="Changer de site effacera l’analyse du produit et vos modifications. L’URL saisie sera conservée."
      confirm-label="Changer de site"
      @confirm="confirmSiteChange"
      @cancel="cancelSiteChange"
    />
  </div>
</template>

<script>
import { computed, nextTick, ref, watch } from "@vue/composition-api";
import {
  mdiLinkVariant,
  mdiMagnify,
  mdiPencil,
  mdiRefresh,
  mdiWeb,
} from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import RmrNotice from "@/components/ui/feedback/RmrNotice.vue";
import RmrSitePicker from "@/components/ui/forms/RmrSitePicker.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";

import CtcOfferCheckStatus from "../shared/CtcOfferCheckStatus.vue";
import CtcProductCard from "../shared/CtcProductCard.vue";

const ERROR_TITLES = {
  invalid_url: "URL invalide",
  unsupported_merchant: "Marchand non pris en charge",
  unsupported: "Marchand non pris en charge",
  analysis_failed: "Analyse impossible",
  blocked: "Le marchand bloque temporairement l’analyse",
  not_found: "Produit introuvable",
  timeout: "Analyse trop longue",
  unparsable: "Fiche produit illisible",
};

const RETRYABLE_ERRORS = [
  "analysis_failed",
  "blocked",
  "timeout",
  "unparsable",
];

export default {
  name: "CtcSourceStep",
  components: {
    CtcOfferCheckStatus,
    CtcProductCard,
    RmrActionButton,
    RmrConfirmDialog,
    RmrNotice,
    RmrSitePicker,
    RmrTextField,
  },
  props: {
    sourceUrl: {
      type: String,
      default: "",
    },
    sites: {
      type: Array,
      default: () => [],
    },
    siteId: {
      type: [Number, String],
      default: null,
    },
    urlError: {
      type: String,
      default: "",
    },
    canAnalyze: {
      type: Boolean,
      default: false,
    },
    analyzing: {
      type: Boolean,
      default: false,
    },
    // { code, message }
    analysisError: {
      type: Object,
      default: null,
    },
    product: {
      type: Object,
      default: null,
    },
    productErrors: {
      type: Object,
      default: () => ({}),
    },
    offerCheck: {
      type: Object,
      default: null,
    },
    checkingOffer: {
      type: Boolean,
      default: false,
    },
    offerCheckError: {
      type: String,
      default: "",
    },
    fromOpportunity: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const confirmingSiteChange = ref(false);
    const choosingSite = ref(!props.siteId);
    const pendingSiteId = ref(null);
    const sourceInput = ref(null);
    const sourcePanel = ref(null);
    const selectedSite = computed(
      () => props.sites.find(({ id }) => id === props.siteId) || null
    );

    const focusSource = () => {
      if (props.fromOpportunity || !props.siteId) return;
      nextTick(() => {
        const input =
          sourceInput.value && sourceInput.value.$el
            ? sourceInput.value.$el.querySelector("input")
            : null;
        if (input) input.focus();
        if (
          sourcePanel.value &&
          window.matchMedia("(max-width: 599px)").matches
        ) {
          const panel = sourcePanel.value.$el || sourcePanel.value;
          panel.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    };

    // Same condition as the offer status: an answer without any usable offer.
    const noUsableOffer = computed(
      () =>
        !props.checkingOffer &&
        !props.offerCheckError &&
        !!props.offerCheck &&
        !props.offerCheck.hasOffer
    );

    // Another product: the URL field, or a new flow for an opportunity.
    const changeProduct = () => {
      if (props.fromOpportunity) {
        emit("change-product");
        return;
      }
      focusSource();
      nextTick(() => {
        const panel =
          sourcePanel.value && (sourcePanel.value.$el || sourcePanel.value);
        if (panel) panel.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    };

    watch(
      () => props.siteId,
      (siteId, previousSiteId) => {
        if (!siteId) {
          choosingSite.value = true;
          return;
        }
        choosingSite.value = false;
        if (siteId !== previousSiteId) focusSource();
      }
    );

    const requestSiteChange = (siteId) => {
      if (siteId === props.siteId) {
        choosingSite.value = false;
        focusSource();
        return;
      }
      if (props.product && !props.fromOpportunity) {
        pendingSiteId.value = siteId;
        confirmingSiteChange.value = true;
        return;
      }
      const site = props.sites.find(({ id }) => id === siteId);
      emit("select-site", siteId, site && site.defaultSignature);
    };

    const confirmSiteChange = () => {
      if (pendingSiteId.value !== null) {
        const site = props.sites.find(({ id }) => id === pendingSiteId.value);
        emit("select-site", pendingSiteId.value, site && site.defaultSignature);
      }
      pendingSiteId.value = null;
    };

    const cancelSiteChange = () => {
      pendingSiteId.value = null;
      choosingSite.value = false;
    };

    return {
      cancelSiteChange,
      choosingSite,
      confirmingSiteChange,
      confirmSiteChange,
      errorTitle: computed(() =>
        props.analysisError
          ? ERROR_TITLES[props.analysisError.code] || "Erreur"
          : ""
      ),
      retryableError: computed(
        () =>
          props.analysisError &&
          RETRYABLE_ERRORS.includes(props.analysisError.code)
      ),
      icons: {
        mdiLinkVariant,
        mdiMagnify,
        mdiPencil,
        mdiRefresh,
        mdiWeb,
      },
      changeProduct,
      noOfferExplanation: computed(
        () =>
          (props.offerCheck && props.offerCheck.reason) ||
          "Affilizz ne trouve actuellement aucune offre pour le produit principal sur le média de ce site."
      ),
      noUsableOffer,
      pendingSiteId,
      requestSiteChange,
      selectedSite,
      sourceInput,
      sourcePanel,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "./_ctc-step.scss";

.ctc-source-panel {
  margin-top: 18px;
  padding: 20px;
  border: 1px solid #dbe3ef;
  border-radius: 18px;
  background: linear-gradient(180deg, #fff 0%, #f8fafc 100%);
  box-shadow: 0 8px 26px rgba(15, 23, 42, 0.05);
}

.ctc-site-selection__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.ctc-site-selection__icon {
  display: flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #eef2ff;
}

.ctc-site-selection__header span:last-child,
.ctc-site-selection__header strong,
.ctc-site-selection__header small {
  display: block;
}

.ctc-site-selection__header strong {
  color: #0f172a;
  font-size: 14px;
}

.ctc-site-selection__header small {
  margin-top: 3px;
  color: #64748b;
  font-size: 11px;
}

.ctc-site-selection__mapping-dot {
  width: 7px;
  height: 7px;
  flex: 0 0 7px;
  border-radius: 50%;
  background: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.ctc-selected-site {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 14px;
  border: 1px solid $rmr-indigo;
  border-radius: $rmr-radius-xl;
  background: $rmr-indigo-bg;
  box-shadow: 0 6px 18px rgba($rmr-indigo, 0.1);
}

.ctc-selected-site__check {
  display: flex;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: $rmr-indigo;
  color: $rmr-surface-0;
  font-size: $rmr-fs-md;
  font-weight: 900;
}

.ctc-selected-site__content {
  min-width: 0;
  flex: 1 1 auto;
}

.ctc-selected-site__content strong,
.ctc-selected-site__content small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ctc-selected-site__content strong {
  color: $rmr-text-primary;
  font-size: $rmr-fs-title;
}

.ctc-selected-site__content small,
.ctc-selected-site__media {
  margin-top: 2px;
  color: $rmr-text-muted;
  font-size: $rmr-fs-sm;
}

.ctc-selected-site__media {
  flex: 0 0 auto;
  margin-top: 0;
  font-weight: 700;
}

.ctc-source-panel__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.ctc-source-panel__icon {
  display: flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #eef2ff;
}

.ctc-source-panel__header span:last-child,
.ctc-source-panel__header strong,
.ctc-source-panel__header small {
  display: block;
}

.ctc-source-panel__header strong {
  color: #0f172a;
  font-size: 14px;
}

.ctc-source-panel__header small {
  margin-top: 3px;
  color: #64748b;
  font-size: 11px;
}

.ctc-source {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.ctc-step__loading--product {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 1px solid #c7d2fe;
  border-radius: 14px;
  background: #eef2ff;
}

.ctc-step__loading--product span,
.ctc-step__loading--product strong,
.ctc-step__loading--product small {
  display: block;
}

.ctc-step__loading--product strong {
  color: #312e81;
  font-size: 14px;
}

.ctc-step__loading--product small {
  margin-top: 3px;
  color: #64748b;
  font-size: 11px;
}

.ctc-source__input {
  flex: 1 1 auto;
  min-width: 0;
}

.ctc-source__submit {
  margin-top: 2px;
}

.ctc-source__no-offer {
  margin-bottom: 12px;
}

.ctc-source__warning {
  margin-bottom: 12px;
}

.ctc-step__offer-check {
  margin-top: 12px;
}

@media (max-width: 599px) {
  .ctc-selected-site {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .ctc-selected-site__media {
    width: calc(100% - 42px);
    margin-left: 42px;
  }

  .ctc-source-panel {
    padding: 16px;
  }

  .ctc-source {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
